import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Link2, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import useToast from "~/components/ui/toast";
import { nextgenSharedConversationPath } from "~/pages/manifold-nextgen/paths";
import {
  type ConversationShare,
  createShareLink,
  deleteShareLink,
  getShareLink,
} from "~/services/conversations";

interface ShareConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
}

const buildShareUrl = (token: string) =>
  `${window.location.origin}${nextgenSharedConversationPath(token)}`;

export const ShareConversationDialog = ({
  open,
  onOpenChange,
  conversationId,
}: ShareConversationDialogProps) => {
  const { showError, showSuccess } = useToast();
  // `undefined` = haven't fetched yet; `null` = fetched, no share exists;
  // `ConversationShare` = fetched, share exists.
  const [share, setShare] = useState<ConversationShare | null | undefined>(undefined);
  const [isMutating, setIsMutating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      return;
    }
    setShare(undefined);
    let cancelled = false;
    void (async () => {
      try {
        const existing = await getShareLink(conversationId);
        if (!cancelled) setShare(existing);
      } catch {
        if (!cancelled) {
          setShare(null);
          showError("Couldn't load share status");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // showError is intentionally excluded: the toast provider doesn't memoize it,
    // so emitting a toast gives a new identity and would re-fire this fetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversationId]);

  const handleGenerate = useCallback(async () => {
    setIsMutating(true);
    try {
      const created = await createShareLink(conversationId);
      setShare(created);
    } catch {
      showError("Couldn't create share link");
    } finally {
      setIsMutating(false);
    }
  }, [conversationId, showError]);

  const handleRevoke = useCallback(async () => {
    setIsMutating(true);
    try {
      await deleteShareLink(conversationId);
      setShare(null);
      setCopied(false);
      showSuccess("Share link revoked");
    } catch {
      showError("Couldn't revoke share link");
    } finally {
      setIsMutating(false);
    }
  }, [conversationId, showError, showSuccess]);

  const handleCopy = useCallback(async () => {
    if (!share) return;
    await navigator.clipboard.writeText(buildShareUrl(share.token));
    setCopied(true);
  }, [share]);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share conversation</DialogTitle>
          <DialogDescription>Anyone with the link can view, read-only.</DialogDescription>
        </DialogHeader>

        {share === undefined && <LoadingSurface />}
        {share === null && <GenerateSurface onGenerate={handleGenerate} isMutating={isMutating} />}
        {share && (
          <div className="flex min-w-0 flex-col gap-2">
            <LinkSurface url={buildShareUrl(share.token)} copied={copied} onCopy={handleCopy} />
            <Button
              variant="destructive"
              size="sm"
              className="self-start"
              onClick={handleRevoke}
              disabled={isMutating}
              loading={isMutating}
            >
              {isMutating ? "Revoking link" : "Revoke link"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const LoadingSurface = () => (
  <div
    className="flex h-24 items-center justify-center rounded-xl border bg-muted/30 text-muted-foreground"
    role="status"
    aria-live="polite"
  >
    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
  </div>
);

interface GenerateSurfaceProps {
  onGenerate: () => void;
  isMutating: boolean;
}

const GenerateSurface = ({ onGenerate, isMutating }: GenerateSurfaceProps) => (
  <button
    type="button"
    onClick={onGenerate}
    disabled={isMutating}
    className="group flex h-24 w-full animate-in fade-in-50 zoom-in-95 flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-gradient-to-br from-muted/40 to-muted/5 transition-all duration-200 cursor-pointer hover:border-primary/40 hover:from-muted/60 disabled:cursor-default disabled:opacity-60"
  >
    <div className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform group-hover:scale-105">
      {isMutating ? (
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      ) : (
        <Link2 className="size-4" aria-hidden="true" />
      )}
    </div>
    <span className="text-sm font-medium text-foreground">
      {isMutating ? "Creating link" : "Generate link"}
    </span>
  </button>
);

interface LinkSurfaceProps {
  url: string;
  copied: boolean;
  onCopy: () => void;
}

const LinkSurface = ({ url, copied, onCopy }: LinkSurfaceProps) => (
  <div className="relative w-full min-w-0 animate-in fade-in-50 zoom-in-95 overflow-hidden rounded-xl border bg-gradient-to-br from-muted/40 to-muted/5 duration-200">
    <p className="min-w-0 select-all truncate py-3 pl-4 pr-12 font-mono text-sm text-foreground">
      {url}
    </p>
    <Button
      variant="ghost"
      size="icon"
      onClick={onCopy}
      aria-label={copied ? "Copied" : "Copy link"}
      className="absolute right-2 top-1/2 size-8 -translate-y-1/2"
    >
      {copied ? <Check className="size-4 text-primary" /> : <Copy className="size-4" />}
    </Button>
  </div>
);
